/**
 * @fileoverview The day a photo says it was taken, and a calendar for disagreeing with it.
 *
 * A small field on the photo screens, with its name inside it; tapped, the month
 * comes up from the bottom. It says "from the photo" when the date came out
 * of the picture's own metadata, which is the one reason to trust it without
 * looking.
 *
 * The panel opens with the month and the year as two lists above the days
 * (owner, 2026-10-14: "I need a better date picker so that i can change the
 * year and day easily"): any month of any year in two taps, then the day. On
 * a phone each list is the system's own picker. The arrows either side still
 * step a month at a time. The date follows them as they change, keeping its
 * own day of the month (held to the month's length and to today), so closing
 * the panel without tapping a day keeps the month and year chosen (owner,
 * 2026-10-14: closing it kept the old date); tapping a day picks that day and
 * closes. The Today / Yesterday / A week ago shortcuts are gone ("not this
 * stupid shortcut"). The month always shows six weeks, so
 * moving between months doesn't change the panel's height, and days after
 * today can't be chosen.
 *
 * Where: EditPhotoDialog and UploadPhotoDialog, above the words.
 * API: none.
 * Settings: the reader's language, for the date's and the months' words.
 * Tunables: PHOTO_TAKEN_YEARS_BACK, how far back the year list reaches.
 * Words (en/es): plants.photo.taken, plants.photo.fromThePhoto, plants.photo.pick.*.
 *
 * @module DateTakenField
 */

import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { BottomPanel } from '@/shared/components/ui/bottom-panel';
import { NativeSelect } from '@/shared/components/ui/native-select';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { formatDate, DateTime } from '@/shared/utils/dateUtils';
import { PHOTO_TAKEN_YEARS_BACK } from '@/config/tunables/ui';
import type { DateSource } from './usePhotoChoice';
import { PHOTO_FIELD } from './photoField';

/** The day shown, where a new one goes, and where it came from. */
interface DateTakenFieldProps {
  /** The day the photo was taken, as a local calendar day. */
  value: Date;
  /** Called with the chosen day, floored to local midnight. */
  onChange: (date: Date) => void;
  /** Shown as a hint when the date came out of the photo's own metadata. */
  source?: DateSource;
  /** While the dialog is busy. */
  disabled?: boolean;
}

/** An arrow either side of the month and year lists: no border, so the lists keep the room. */
const STEP_BUTTON = 'inline-flex h-12 w-7 shrink-0 items-center justify-center rounded-md text-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-30';

/**
 * The month and year lists' own text: a size smaller and a narrower gap for
 * the arrow, so "September" fits whole at the largest text size on a narrow
 * phone (owner, 2026-10-14: at the largest font the month was cut off).
 */
const LIST_TEXT = 'pl-2.5 pr-8 text-sm';

/** The month calendar's own caption, left out: the lists above it say the month. */
const NO_CAPTION = (): null => null;

/**
 * The days fill the panel's width, each a finger-sized square, so the month
 * lines up under the lists above it (the calendar's own days are 36px wide,
 * narrower than the lists).
 */
const FULL_WIDTH_DAYS = {
  months: 'w-full',
  month: 'w-full space-y-2',
  table: 'w-full border-collapse',
  head_row: 'flex',
  head_cell: 'flex-1 text-center text-[0.8rem] font-normal text-muted-foreground',
  row: 'mt-1 flex w-full',
  cell: 'relative h-11 flex-1 p-0 text-center text-sm focus-within:z-20',
  day: 'inline-flex h-11 w-full items-center justify-center rounded-md p-0 font-normal hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring aria-selected:opacity-100',
};

/**
 * The "Taken" field, and the calendar that opens from it.
 *
 * @param props - DateTakenFieldProps.
 */
export const DateTakenField: React.FC<DateTakenFieldProps> = ({
  value, onChange, source, disabled,
}) => {
  const { reactTranslate: t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  /** The month the calendar shows, its first day; set to the photo's month each time the panel opens. */
  const [shown, setShown] = React.useState(() => DateTime.fromJSDate(value).startOf('month'));
  /** Today, as a local day: the latest day a photo can have been taken. */
  const today = DateTime.local().startOf('day');
  /** This month, the latest the calendar can show. */
  const thisMonth = today.startOf('month');
  /** The earliest year in the list: PHOTO_TAKEN_YEARS_BACK ago, or the photo's own year if older. */
  const firstYear = Math.min(today.year - PHOTO_TAKEN_YEARS_BACK, DateTime.fromJSDate(value).year);
  /** The years to choose from, this year first. */
  const years = Array.from({ length: today.year - firstYear + 1 }, (_, i) => today.year - i);
  /** January to December, in the reader's language. */
  const months = Array.from({ length: 12 }, (_, i) => DateTime.local(2000, i + 1, 1).toFormat('LLLL'));

  /**
   * Opens the panel on the photo's own month.
   */
  const openPanel = (): void => {
    setShown(DateTime.fromJSDate(value).startOf('month'));
    setOpen(true);
  };

  /**
   * Shows another month, never one after this month, and moves the date into
   * it: the same day of the month, held to the month's length and to today.
   *
   * @param month - Any day in the month to show.
   */
  const show = (month: DateTime): void => {
    const first = month.startOf('month');
    const target = first > thisMonth ? thisMonth : first;
    setShown(target);
    const current = DateTime.fromJSDate(value);
    const moved = target.set({ day: Math.min(current.day, target.daysInMonth ?? 28) });
    const day = moved > today ? today : moved;
    if (!day.hasSame(current, 'day')) onChange(day.startOf('day').toJSDate());
  };

  /**
   * Chooses a day and closes the panel.
   *
   * @param day - The day, floored to local midnight.
   */
  const choose = (day: DateTime): void => {
    setOpen(false);
    onChange(day.startOf('day').toJSDate());
  };

  return (
    <>
      <button type="button" onClick={openPanel} disabled={disabled} className={PHOTO_FIELD}>
        <span className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
          {t('plants.photo.taken')}
          {source === 'exif' && (
            <span className="truncate text-primary">{t('plants.photo.fromThePhoto')}</span>
          )}
        </span>
        {/*
          * Read straight off the Date, not round-tripped through an ISO
          * string. formatDateLong(parseDate(value.toISOString())) took a
          * date that means "the twelfth", wrote it as midnight UTC, and
          * read it back in a zone five hours behind — so the field said
          * the eleventh while the card, a few pixels away, said the
          * twelfth. The date saved was right the whole time; only this
          * line was lying about it.
          */}
        <span className="truncate text-[0.9375rem] font-medium">
          {formatDate(DateTime.fromJSDate(value), true)}
        </span>
      </button>

      <BottomPanel open={open} onOpenChange={setOpen} title={t('plants.photo.taken')} className="sm:max-w-fit">
        <div className="mx-auto w-[21rem] max-w-full">
          {/* The month and the year, each a list; the arrows step a month at a time. */}
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => show(shown.minus({ months: 1 }))}
              disabled={shown.year === firstYear && shown.month === 1}
              aria-label={t('plants.photo.pick.earlierMonth')}
              className={STEP_BUTTON}
            >
              <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            </button>
            <NativeSelect
              className="min-w-0 flex-1"
              selectClassName={LIST_TEXT}
              value={shown.month}
              onChange={(e) => show(shown.set({ month: Number(e.target.value) }))}
              aria-label={t('plants.photo.pick.month')}
            >
              {months.map((name, i) => (
                <option key={name} value={i + 1} disabled={shown.year === today.year && i + 1 > today.month}>{name}</option>
              ))}
            </NativeSelect>
            <NativeSelect
              className="w-[5rem] shrink-0"
              selectClassName={LIST_TEXT}
              value={shown.year}
              onChange={(e) => show(shown.set({ year: Number(e.target.value) }))}
              aria-label={t('plants.photo.pick.year')}
            >
              {years.map((year) => <option key={year} value={year}>{year}</option>)}
            </NativeSelect>
            <button
              type="button"
              onClick={() => show(shown.plus({ months: 1 }))}
              disabled={shown >= thisMonth}
              aria-label={t('plants.photo.pick.laterMonth')}
              className={STEP_BUTTON}
            >
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
          {/* Six weeks always, so the panel keeps its height from month to month; no days after today. */}
          <CalendarComponent
            mode="single"
            selected={value}
            month={shown.toJSDate()}
            onMonthChange={(month: Date) => show(DateTime.fromJSDate(month))}
            fixedWeeks
            showOutsideDays
            toDate={today.toJSDate()}
            components={{ Caption: NO_CAPTION }}
            className="px-0 pb-0 pt-3"
            classNames={FULL_WIDTH_DAYS}
            initialFocus
            onSelect={(date: Date | undefined) => {
              // The day already chosen comes back as undefined (the calendar would clear it): tapping it keeps it and closes.
              choose(date ? DateTime.fromJSDate(date) : DateTime.fromJSDate(value));
            }}
          />
        </div>
      </BottomPanel>
    </>
  );
};
